import React, { useEffect, useState } from "react";
import TableComponent from "../../common/TableComponent/TableComponent";
import IconButton from "../../common/IconButton/IconButton";
import PlusIcon from "../../../assets/PlusIcon";
import ModalComponent from "../../common/ModalComponent/ModalComponent";
import { toast } from "react-toastify";
import Loader from "../../common/Loader/Loader";
import { DataTableComponent } from "../../common/TableComponent/DataTableComponent";
import { formatDate } from "../../../utils/dateFormat";
import "./ExternalUserManagement.css";
import { createNewUser, revokeUser, getExternalUserList } from "../../../services/foundation/externalUser-service";
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';
import { useTranslation } from "react-i18next";
import validateFields from "../../../utils/ValidationInput";

const ExternalUserManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    comments: "",
    phoneNumber: "",
  });
  const [editingUser, setEditingUser] = useState(null);

  const isEditMode = modalType === "edit";
  const isAddMode = modalType === "add";
  const isRevokeMode = modalType === "revoke";

  const columns = [
    { name: t("emailId"), field: "email", selector: row => row.email, sortable: true, width: "200px" },
    { name: t("firstName"), field: "firstName", selector: row => row.firstName, sortable: true },
    { name: t("lastName"), field: "lastName", selector: row => row.lastName, sortable: true },
    { name: t("addedDate"), field: "createdDate", selector: row => row.createdDate, cell: row => formatDate(row.createdDate), sortable: true },
    // { name: t("addedBy"), field: "addedBy", selector: row => row.addedBy, sortable: true },
    { name: t("comments"), field: "comments", selector: row => row.comments, sortable: true },
  ];

  const fieldsData = [
    { fieldName: t("email"), name: "email", type: "email", disabled: (modalType === "revoke" || modalType === "edit"), required: true },
    { fieldName: t("firstName"), name: "firstName", type: "input", required: true },
    { fieldName: t("lastName"), name: "lastName", type: "input", required: true },
    { fieldName: t("phoneNumber"), name: "phoneNumber", type: "phoneNumber", required: true },
    { fieldName: t("comments"), name: "comments", type: "textarea" },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await getExternalUserList();
      const usersArray = Array.isArray(res) ? res : res.userList || res.data || [];
      setUsers(usersArray);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (type, data = null) => {
    setModalType(type);
    if (data) {
      setFormData({
        email: data.email || "",
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        comments: data.comments || "",
        phoneNumber: data.phoneNumber || "",
        userId: data.id || "",
      });
      setEditingUser(data);
    } else {
      setFormData({ email: "", firstName: "", lastName: "", comments: "", phoneNumber: "" });
      setEditingUser(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({ email: "", firstName: "", lastName: "", comments: "", phoneNumber: "" });
    setEditingUser(null);
  };

  const handleInputChange = (target) => {
    const { name, value } = target;
    toast.dismiss();
    if (name === "firstName" && value.length > 30) {
      toast.error(t("firstNameCannotExceed", { number: 30 }), { position: "top-center" });
      return;
    }
    if (name === "lastName" && value.length > 30) {
      toast.error(t("lastNameCannotExceed", { number: 30 }), { position: "top-center" });
      return;
    }
    if (name === "comments" && value.length > 255) {
      toast.error(t("commentsCannotExceed", { number: 255 }), { position: "top-center" });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [target.name]: target.value,
    }));
  };

  const onChangePhoneNumber = (value) => {
    setFormData((prev) => ({
      ...prev,
      ['phoneNumber']: value,
    }));
  };

  const trimText = (text) => {
    return text.trim().replace(/\s+/g, " ");
  };

  const handleAddUser = async () => {
    toast.dismiss();

    const requiredFields = [
      { label:t("email"), key: "Email", type: "email", value: trimText(formData.email) },
      { label:t("firstName"), key: "First Name", type: "text", value: trimText(formData.firstName) },
      { label:t("lastName"), key: "Last Name", type: "text", value: trimText(formData.lastName) },
      { label:t("phoneNumber"), key: "Phone Number", type: "", value: trimText(formData.phoneNumber) },
    ];
    const isValid = validateFields(formData, requiredFields, 'byValue');
    if (!isValid) {
      setLoading(false);
      return;
    } else {
      if (formData?.phoneNumber && !isValidPhoneNumber(formData?.phoneNumber.toString())) {
        toast.error(t("phoneNumberInvalid"), { position: "top-center" });
        setLoading(false);
        return;
      }
    }

    let localNumber = formData?.phoneNumber;
    if (formData?.phoneNumber) {
      const updatedPhoneNumber = parsePhoneNumberFromString(formData.phoneNumber);
      if (updatedPhoneNumber) {
        localNumber = updatedPhoneNumber?.nationalNumber;
      }
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        userName: formData.firstName + formData.lastName,
        phoneNumber: localNumber,
      }
      const response = await createNewUser(payload);
      if (response) {
        handleCloseModal();
        fetchUsers();
      }
    } catch (error) {
      toast.error(t("addUserError"), { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = () => {
    setLoading(true);
    try {
      setUsers((prev) =>
        prev.map((user) =>
          user.email === editingUser.email
            ? { ...user, ...formData }
            : user
        )
      );
      handleCloseModal();
    } catch (error) {
      toast.error(t("updateUserError"), { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeUser = async () => {
    setLoading(true);
    try {
      const response = await revokeUser(editingUser?.id, editingUser?.email);
      if (response) {
        handleCloseModal();
        fetchUsers();
      }
    } catch (error) {
      toast.error(t("revokeUserError"), { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="external-manag-Container">
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="surveyHeader">
            <h2 className="surveyTitle">{t("listOfExternalUsers")}</h2>
            <IconButton
              text={t("addExternalUser")}
              onClick={() => handleOpenModal("add")}
              style={{
                backgroundColor: "#F0F0F0",
                color: "#454647",
                border: "1px solid #B3B2B4",
              }}
              icon={<PlusIcon />}
            />
          </div>
          <hr></hr>

          <div style={{ marginTop: "1rem" }}>
            <DataTableComponent
              columns={columns}
              data={users}
              tableName="ExternalUserManagement"
              onEditDetails={(row) => handleOpenModal("edit", row)}
              onRevoke={(row) => handleOpenModal("revoke", row)}
              pageCount={5}
            />
          </div>

          <ModalComponent
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            modalTitle={
              modalType === "add"
                ? t("addExternalUser")
                : modalType === "edit"
                  ? t("editExternalUser")
                  : t("revokeExternalUser")
            }
            modalText={
              modalType === "revoke" && (
                <p>
                  {t("revokeUserConfirmation")} <strong>{formData?.email}</strong>?
                </p>
              )
            }
            fieldsData={modalType === "revoke" ? [] : fieldsData}
            formData={formData}
            onChangeInput={handleInputChange}
            onChangePhoneNumber={onChangePhoneNumber}
            buttons={[
              (isAddMode || isEditMode) && (
                <button
                  key="addOrEdit"
                  className="saveButton"
                  onClick={isEditMode ? handleEditUser : handleAddUser}
                >
                  {isEditMode ? t("update") : t("add")}
                </button>
              ),
              isRevokeMode && (
                <button key="revoke" className="saveButton" onClick={handleRevokeUser}>
                  {t("revoke")}
                </button>
              ),
              <button key="cancel" className="cancelButton" onClick={handleCloseModal}>
                {t("cancel")}
              </button>,
            ].filter(Boolean)}
          />
        </>
      )}
    </div>
  );
};

export default ExternalUserManagement;